import React, { Component } from "react"
import PropTypes from "prop-types"
import d3cloud from "d3.layout.cloud"
import isEqual from "lodash.isequal"

import { QUORUM_BLUE, MIDDLE_PURPLE } from "shared/imports/sharedConstants"
import "app/static/stylus/wordCloud.styl"

export default class WordCloud extends Component {
    static propTypes = {
        isLoading: PropTypes.bool.isRequired,
        updateGraph: PropTypes.func.isRequired,
        data: PropTypes.object,
        hiddenTerms: PropTypes.object,
    }

    static defaultProps = {
        colorConfigs: {
            primaryColor: QUORUM_BLUE,
            secondaryColor: MIDDLE_PURPLE,
        },
    }

    componentDidMount() {
        this.setupGraph()
    }

    componentDidUpdate(oldProps) {
        const loadingFinished = oldProps.isLoading && !this.props.isLoading
        const colorChange = !isEqual(oldProps.colorConfigs, this.props.colorConfigs)

        if (loadingFinished || !this.props.data.equals(oldProps.data)) {
            this.createWordCloud()
        } else if (!this.props.hiddenTerms.equals(oldProps.hiddenTerms)) {
            this.hideWords(this.props.hiddenTerms.toJS())
        } else if (colorChange) {
            this.recolorWords()
        }
    }

    componentWillUnmount() {
        d3.select(".d3-tip").remove()
    }

    setupGraph() {
        this.svgWidth = parseInt(d3.select(this.node).style("width"))
        this.svgHeight = parseInt(d3.select(this.node).style("height"))

        d3.select(this.node)
            .attr("width", `${this.svgWidth}px`)
            .attr("height", `${this.svgHeight}px`)
            .append("g")
            .attr("transform", `translate(${this.svgWidth / 2},${this.svgHeight / 2})`)

        this.fontSizeScale = d3.scale.log().range([10, 100])
    }

    applyData = (arrangedWords) => {
        const colors = [this.props.colorConfigs.primaryColor, this.props.colorConfigs.secondaryColor]

        const wordGroup = d3.select(this.node).select("g").selectAll("text").data(arrangedWords)

        const tooltip = d3
            .tip()
            .attr("class", "d3-tip")
            .offset(function (d) {
                if (d.rotate) {
                    // this is a rough approximation, can't
                    // figure out how to center it for the life of me
                    // so instead we're just removing the arrow
                    return [-d.height / 2, -d.height / 3]
                } else if (d.fontSize < 20) {
                    return [-20, 0]
                }
                return [0, 0]
            })
            .html((d) => {
                const text = [
                    `<div>${d.long_display}: ${d.value}<div>`,
                    "<hr>",
                    "<i>Double click to remove</br>word from visualization</i>",
                ]

                return text.join("")
            })

        d3.select(this.node).call(tooltip)

        // styling on .cloud-text disables highlighting of word
        // on double click
        wordGroup
            .enter()
            .append("text")
            .attr("id", (d, i) => i)
            .style("font-size", (d) => {
                d.fontSize = this.fontSizeScale(d.value)
                return `${d.fontSize}px`
            })
            .style("fill", (d) => {
                d.color = colors[Math.round(Math.random())]
                return d.color
            })
            .attr("text-anchor", "middle")
            .attr("class", "cloud-text")
            .attr("transform", (d) => `translate(${d.x},${d.y})rotate(${d.rotate})`)
            .text((d) => d.id)
            .style("opacity", 1e-6)
            .on("mouseover", function (d) {
                // needs to be an anonymous function, not an arrow function
                // due to autobinding messing things up. Also can be
                // fixed with d3 v4 but we're not on that.
                tooltip.show(d)
                d3.select(this).style("opacity", "0.5")
            })
            .on("mouseout", function (d) {
                tooltip.hide(d)
                d3.select(this).style("opacity", "1")
            })
            .on("dblclick", (d) => {
                tooltip.hide(d)
                this.props.updateGraph(d.id, d.fontSize)
            })

        wordGroup.transition().duration(1000).style("opacity", 1)

        wordGroup.exit().remove()
    }

    createWordCloud() {
        const data = this.props.data.toJS()
        this.fontSizeScale.domain(d3.extent(data.map((d) => d.value)))

        d3.select(this.node).select("g").selectAll("text").remove()

        d3cloud()
            .size([this.svgWidth, this.svgHeight])
            .words(data)
            .text((d) => d.id)
            .padding(3)
            .rotate(() => (Math.random() * 3 > 1 ? 0 : 90))
            .font('"Helvetica Neue", Helvetica')
            .fontSize((d) => this.fontSizeScale(d.value))
            .on("end", this.applyData)
            .start()
    }

    recolorWords() {
        const colors = [this.props.colorConfigs.primaryColor, this.props.colorConfigs.secondaryColor]

        d3.select(this.node)
            .select("g")
            .selectAll("text")
            .style("fill", (d) => {
                d.color = colors[Math.round(Math.random())]
                return d.color
            })
    }

    hideWords() {
        d3.select(this.node)
            .select("g")
            .selectAll("text")
            .filter((d) => this.props.hiddenTerms.includes(d.id))
            .remove()
    }

    render() {
        return <svg ref={(wc) => (this.node = wc)} id={this.props.svgId} />
    }
}
